import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactFormModal from "./ContactFormModal";

interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  service: {
    title: string;
    icon: React.ReactElement;
    description: string;
    detailedDescription: string;
    features: string[];
    benefits: string[];
    idealFor: string[];
  } | null;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
  open,
  onClose,
  service,
}) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  if (!service) return null;

  const handleGetStarted = () => {
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  // Generate templated message based on service
  const getContactTemplate = () => {
    const subject = `Inquiry about ${service.title}`;
    const message = `Hello,

I am interested in learning more about your ${service.title}.

Please provide me with information regarding:
- Pricing options
- Setup timeline
- Technical specifications
- Support options

Additional details about my requirements:
[Please describe your specific needs, expected traffic, storage requirements, etc.]

Thank you for your time.`;

    return { subject, message };
  };

  const { subject, message } = getContactTemplate();

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderRadius: 3,
          border: "1px solid rgba(0, 168, 255, 0.3)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          borderBottom: "1px solid rgba(0, 168, 255, 0.2)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: "12px",
            background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(service.icon, {
            sx: { fontSize: 32, color: "white" },
          })}
        </Box>
        <Typography
          component="span"
          variant="h5"
          sx={{ fontWeight: 600, flexGrow: 1 }}
        >
          {service.title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: "text.secondary",
            "&:hover": {
              color: "primary.main",
              bgcolor: "rgba(0, 168, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mb: 3, lineHeight: 1.8 }}
        >
          {service.detailedDescription}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Key Features
          </Typography>
          <List sx={{ py: 0 }}>
            {service.features.map((feature, index) => (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleIcon
                    sx={{ color: "primary.main", fontSize: 20 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { color: "text.secondary" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Benefits
          </Typography>
          <List sx={{ py: 0 }}>
            {service.benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleIcon
                    sx={{ color: "primary.main", fontSize: 20 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={benefit}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { color: "text.secondary" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box
          sx={{
            p: 3,
            bgcolor: "rgba(0, 168, 255, 0.05)",
            borderRadius: 2,
            border: "1px solid rgba(0, 168, 255, 0.2)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Ideal For
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", lineHeight: 1.8 }}
          >
            {service.idealFor.join(" • ")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            "&:hover": {
              borderColor: "primary.light",
              bgcolor: "rgba(0, 168, 255, 0.1)",
            },
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleGetStarted}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5dcdff 0%, #3385db 100%)",
              boxShadow: "0 8px 24px rgba(0, 168, 255, 0.4)",
            },
          }}
        >
          Get Started
        </Button>
      </DialogActions>
    </Dialog>

    <ContactFormModal
      open={contactModalOpen}
      onClose={handleCloseContactModal}
      templateSubject={subject}
      templateMessage={message}
      showDatacenterOptions={service.title !== "Domain Management"}
      isDomainManagement={service.title === "Domain Management"}
      isCMSHosting={service.title === "Content Management Solution Hosting"}
    />
    </>
  );
};

export default ServiceModal;
